import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, FolderPlus } from 'lucide-react'
import type { PatientResume } from '../lib/types'
import { Modale } from './base'
import SelecteurPatient from './SelecteurPatient'
import {
  FormulaireFichier,
  FormulaireTexte,
  type CibleDepot,
  type Retour,
} from './dossier/Documents'

/**
 * Ajout d'un document hors d'un dossier : on demande d'abord à qui il
 * appartient. Si la personne n'a pas encore de dossier, il est créé avec sa
 * seule référence, et l'analyse proposera ensuite l'identité lue dans le
 * document — à valider comme le reste.
 */
export default function AjoutDocument({
  quoi,
  fermer,
}: {
  quoi: 'fichier' | 'texte'
  fermer: () => void
}) {
  const naviguer = useNavigate()
  const [etape, setEtape] = useState<'choix' | 'dossier' | 'formulaire'>('choix')
  const [cible, setCible] = useState<CibleDepot | null>(null)

  const apresEnvoi = (retour: Retour) => {
    fermer()
    naviguer(
      retour.analyseId ? `/verification/${retour.analyseId}` : `/patients/${retour.patientId}`,
    )
  }

  if (etape === 'choix') {
    return (
      <Modale
        titre={quoi === 'texte' ? 'Coller un texte' : 'Ajouter un document'}
        sousTitre="À quel dossier ce document appartient-il ?"
        fermer={fermer}
      >
        <div className="flex flex-col gap-2">
          <button
            className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3.5 text-left hover:bg-bleu-50"
            onClick={() => setEtape('dossier')}
          >
            <FolderOpen className="mt-0.5 h-5 w-5 text-bleu-500" />
            <span>
              <span className="block text-[14px] font-medium">Un dossier existant</span>
              <span className="block text-[12.5px] text-brume">
                Chercher la patiente par nom, prénom ou référence.
              </span>
            </span>
          </button>

          <button
            className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3.5 text-left hover:bg-bleu-50"
            onClick={() => {
              setCible({ nouveauDossier: true })
              setEtape('formulaire')
            }}
          >
            <FolderPlus className="mt-0.5 h-5 w-5 text-bleu-500" />
            <span>
              <span className="block text-[14px] font-medium">
                Une patiente qui n’a pas encore de dossier
              </span>
              <span className="block text-[12.5px] text-brume">
                Le dossier est créé avec sa référence, et l’analyse te proposera l’identité lue dans
                le document. Rien n’est enregistré sans ta validation.
              </span>
            </span>
          </button>
        </div>
      </Modale>
    )
  }

  if (etape === 'dossier') {
    return (
      <SelecteurPatient
        titre="Dans quel dossier ?"
        fermer={fermer}
        choisir={(patient: PatientResume) => {
          setCible({ patientId: patient.id })
          setEtape('formulaire')
        }}
      />
    )
  }

  if (!cible) return null

  return quoi === 'fichier' ? (
    <FormulaireFichier cible={cible} fermer={fermer} apresEnvoi={apresEnvoi} />
  ) : (
    <FormulaireTexte cible={cible} fermer={fermer} apresEnvoi={apresEnvoi} />
  )
}
